import React, { useEffect } from 'react'
import { t } from 'ttag'

import PropTypes from 'prop-types'
import { useDispatch, useSelector } from 'react-redux'
import styled from 'styled-components/macro'

import LoadingSpinner from 'components/ui/LoadingSpinner'
import { programmesList, updateSelectedFilters } from 'store/actions/filters'
import Button from 'components/ui/Button'
import FilterTitle from 'components/ui/FilterTitle'
import SearchFilter from 'components/ui/SearchFilter'
import SelectedFilter from 'components/ui/SelectedFilter'
import FilterText from 'components/ui/FilterText'
import RemoveIcon from 'components/ui/RemoveIcon'
import SelectedFilterContainer from 'components/ui/SelectedFilterContainer'
import {
  OTHER_EUSBSR_RELATED,
  OTHER_EUSDR_RELATED,
  SELECTED_FILTER_MAX_LINES
} from 'store/constants/constants'
import Dotdotdot from 'react-dotdotdot'
import CustomDropDownTreeSelect from 'components/ui/CustomDropdownTreeSelect'

const SpinnerContainer = styled.div`
  min-height: 20px;
  position: relative;
`

function ProgrammeHierarchyMultiSelect({
  type,
  values,
  title,
  onClose,
  showFilter,
  keyWord,
  customTop
}) {
  const filters = useSelector(state => state.filters)
  const { currentSearchable, programmes, isProgrammesPending, programmeFilters } = filters
  const filterObject = filters.selectedFilters[currentSearchable][type]
  return (
    <PureProgrammeHierarchyMultiSelect
      type={type}
      values={values}
      title={title}
      onClose={onClose}
      showFilter={showFilter}
      keyWord={keyWord}
      customTop={customTop}
      filterObject={filterObject}
      programmes={programmes}
      isProgrammesPending={isProgrammesPending}
      programmeFilters={programmeFilters}
    />
  )
}

const PureProgrammeHierarchyMultiSelect = React.memo(function PureProgrammeHierarchyMultiSelect({
  type,
  values,
  title,
  onClose,
  showFilter,
  keyWord,
  customTop,
  filterObject,
  programmes,
  isProgrammesPending,
  programmeFilters
}) {
  const dispatch = useDispatch()
  const selectedValues = filterObject[values[0]]

  let options = programmes
    ? programmes.map(option => {
        const parentOption = {
          label: option[keyWord],
          value: option.id,
          expanded: true
        }
        const selectedOption = selectedValues.filter(
          s => s.id === option.id && s.label === option[keyWord]
        )
        if (selectedOption.length > 0) {
          parentOption.checked = true
        }
        return parentOption
      })
    : []

  let shouldFetchProgrammeOptions = true

  function checkDynamicOptions(currentOptions, selectedTypes) {
    let dynamicOptions = currentOptions
    if (
      selectedTypes.includes(OTHER_EUSDR_RELATED) ||
      selectedTypes.includes(OTHER_EUSBSR_RELATED)
    ) {
      dynamicOptions = []
      shouldFetchProgrammeOptions = false
    }
    options = dynamicOptions
  }

  const { type: selectedTypes } = filterObject

  if (options.length > 0) {
    checkDynamicOptions(
      options,
      selectedTypes.map(selectedType => selectedType.label)
    )
  }

  const isLoading = isProgrammesPending

  useEffect(() => {
    if (showFilter && shouldFetchProgrammeOptions) {
      const { types } = programmeFilters
      const { periods } = programmeFilters
      const programmeParams = {
        published_projects: true,
        search_filter: true,
        ...(types && { types }),
        ...(periods && { periods })
      }
      dispatch(programmesList(programmeParams))
    }
  }, [dispatch, programmeFilters, shouldFetchProgrammeOptions, showFilter])

  const onChange = (currentNode, selectedNodes) => {
    const filterValue = selectedNodes.map(node => ({
      id: node.value,
      label: node.label
    }))

    const data = {
      filterType: type,
      filterId: values[0],
      filterValue
    }
    dispatch(updateSelectedFilters(data))
  }

  function handleFilterRemove(filterId, filterValue, valueIsArray = false) {
    let resultingValue = null
    if (valueIsArray) {
      resultingValue = filterObject[filterId].filter(
        s => s.id !== filterValue.id || s !== filterValue
      )
    }
    const data = {
      filterType: type,
      filterId,
      filterValue: resultingValue
    }
    dispatch(updateSelectedFilters(data))
  }

  function renderSelectedValues() {
    return values.map(value => {
      if (filterObject[value]) {
        const filterValue = filterObject[value]
        if (filterValue instanceof Array) {
          return filterValue.map(v => (
            <SelectedFilterContainer className="selected-filters" key={v.id ? v.id : v}>
              <SelectedFilter>
                <FilterText title={v.label ? v.label : v}>
                  <Dotdotdot clamp={SELECTED_FILTER_MAX_LINES}>{v.label ? v.label : v}</Dotdotdot>
                </FilterText>
                <RemoveIcon
                  className="far fa-times"
                  onClick={() => handleFilterRemove(value, v, true)}
                />
              </SelectedFilter>
            </SelectedFilterContainer>
          ))
        }
      }
      return false
    })
  }

  return (
    <>
      <SearchFilter open={showFilter} customTop={customTop}>
        <FilterTitle>{title}</FilterTitle>
        {isLoading ? (
          <SpinnerContainer>
            <LoadingSpinner showText={false} />
          </SpinnerContainer>
        ) : (
          <CustomDropDownTreeSelect data={options} onChange={onChange} />
        )}

        <Button size="medium" onClick={onClose}>
          {t`Close`}
        </Button>
      </SearchFilter>
      <div>{renderSelectedValues()}</div>
    </>
  )
})

ProgrammeHierarchyMultiSelect.propTypes = {
  type: PropTypes.string,
  values: PropTypes.array,
  title: PropTypes.string,
  onClose: PropTypes.func,
  showFilter: PropTypes.bool,
  keyWord: PropTypes.string,
  customTop: PropTypes.string
}

PureProgrammeHierarchyMultiSelect.propTypes = {
  type: PropTypes.string,
  values: PropTypes.array,
  title: PropTypes.string,
  onClose: PropTypes.func,
  showFilter: PropTypes.bool,
  keyWord: PropTypes.string,
  customTop: PropTypes.string,
  filterObject: PropTypes.object,
  programmes: PropTypes.array,
  isProgrammesPending: PropTypes.bool,
  programmeFilters: PropTypes.object
}

export default ProgrammeHierarchyMultiSelect
